import * as THREE from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export interface CpuModifierDef {
  type: string;
  enabled: boolean;
  params: Record<string, number | boolean | string>;
}

export function applyCpuModifiers(
  geometry: THREE.BufferGeometry,
  modifiers: CpuModifierDef[],
): THREE.BufferGeometry {
  let geom = geometry;
  for (const mod of modifiers) {
    if (!mod.enabled) continue;
    switch (mod.type) {
      case 'smooth':
        geom = applySmooth(geom, mod.params);
        break;
      case 'normal':
        geom = applyNormal(geom, mod.params);
        break;
      case 'optimize':
        geom = applyOptimize(geom, mod.params);
        break;
      case 'shell':
        geom = applyShell(geom, mod.params);
        break;
      case 'slice':
        geom = applySlice(geom, mod.params);
        break;
      case 'uvPlanar':
        geom = applyUvPlanar(geom, mod.params);
        break;
      case 'uvBox':
        geom = applyUvBox(geom, mod.params);
        break;
      case 'uvCylindrical':
        geom = applyUvCylindrical(geom, mod.params);
        break;
      case 'uvOrganic':
        geom = applyUvOrganic(geom, mod.params);
        break;
      case 'uvTransformMod':
        geom = applyUvTransform(geom, mod.params);
        break;
    }
  }
  // Shell / UV mods can leave a cloned tangent attribute that no longer matches
  // normals/UVs. Drop it and rebuild — do not recompute vertex normals or weld
  // (that made Cap / cage reflections wavy and quilted).
  return rebuildTangentsAfterCpuModifiers(geom);
}

/** Drop stale tangents and rebuild MikkT UV tangents when possible. */
export function rebuildTangentsAfterCpuModifiers(geom: THREE.BufferGeometry): THREE.BufferGeometry {
  geom.deleteAttribute('tangent');
  ensureGeometryTangents(geom, { force: true });
  return geom;
}

/**
 * Ensure a BufferGeometry has a tangent attribute (MikkT when UV/index allow, else
 * normal-derived fallback).
 *
 * `force: true` — discard authored tangents and rebuild. Required for anisotropy on
 * imported GLBs: many files ship a TANGENT accessor that is wrong for Three.js, while
 * primitives (Sphere) have none so MikkT already ran and aniso looked fine there.
 */
export function ensureGeometryTangents(
  geom: THREE.BufferGeometry,
  options?: { force?: boolean },
): void {
  if (!geom) return;

  if (!geom.index && geom.hasAttribute('position')) {
    const count = geom.getAttribute('position').count;
    const idx = count > 65535 ? new Uint32Array(count) : new Uint16Array(count);
    for (let i = 0; i < count; i++) idx[i] = i;
    geom.setIndex(new THREE.BufferAttribute(idx, 1));
  }

  const force = options?.force === true;
  if (!force && geom.hasAttribute('tangent')) return;

  const canCompute =
    !!geom.index &&
    geom.hasAttribute('position') &&
    geom.hasAttribute('normal') &&
    geom.hasAttribute('uv');
  if (canCompute) {
    try {
      geom.deleteAttribute('tangent');
      geom.computeTangents();
    } catch {
      /* fall through */
    }
    if (geom.hasAttribute('tangent')) return;
  }

  const normals = geom.getAttribute('normal');
  if (!normals) return;
  const count = normals.count;
  const tangents = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    const nx = normals.getX(i);
    const ny = normals.getY(i);
    const nz = normals.getZ(i);
    let tx: number;
    let ty: number;
    let tz: number;
    if (Math.abs(ny) < 0.9) {
      tx = -nz;
      ty = 0;
      tz = nx;
    } else {
      tx = 0;
      ty = nz;
      tz = -ny;
    }
    const len = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
    tangents[i * 4] = tx / len;
    tangents[i * 4 + 1] = ty / len;
    tangents[i * 4 + 2] = tz / len;
    tangents[i * 4 + 3] = 1;
  }
  geom.setAttribute('tangent', new THREE.BufferAttribute(tangents, 4));
}

function applySmooth(
  geom: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const iterations = (params.iterations as number) || 1;
  const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
  if (!posAttr) return geom;

  const indexed = geom.index !== null;
  const positions = new Float32Array(posAttr.array);
  const count = posAttr.count;

  const adjacency = buildAdjacency(geom);

  for (let iter = 0; iter < iterations; iter++) {
    const smoothed = new Float32Array(positions.length);
    for (let i = 0; i < count; i++) {
      const neighbors = adjacency.get(i);
      if (!neighbors || neighbors.size === 0) {
        smoothed[i * 3] = positions[i * 3];
        smoothed[i * 3 + 1] = positions[i * 3 + 1];
        smoothed[i * 3 + 2] = positions[i * 3 + 2];
        continue;
      }
      let sx = positions[i * 3], sy = positions[i * 3 + 1], sz = positions[i * 3 + 2];
      let n = 1;
      for (const nb of neighbors) {
        sx += positions[nb * 3];
        sy += positions[nb * 3 + 1];
        sz += positions[nb * 3 + 2];
        n++;
      }
      smoothed[i * 3] = sx / n;
      smoothed[i * 3 + 1] = sy / n;
      smoothed[i * 3 + 2] = sz / n;
    }
    positions.set(smoothed);
  }

  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.computeVertexNormals();
  return geom;
}

function applyNormal(
  geom: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const mode = (params.mode as string) || 'autoSmooth';
  const flip = (params.flip as boolean) ?? false;
  const angleDeg = (params.angle as number) ?? 30;
  const angleRad = (angleDeg * Math.PI) / 180;

  if (flip) {
    const idx = geom.index;
    if (idx) {
      const arr = Array.from(idx.array);
      for (let i = 0; i < arr.length; i += 3) {
        const tmp = arr[i + 1];
        arr[i + 1] = arr[i + 2];
        arr[i + 2] = tmp;
      }
      geom.setIndex(arr);
    }
    const normAttr = geom.getAttribute('normal') as THREE.BufferAttribute | null;
    if (normAttr) {
      const normals = new Float32Array(normAttr.array.length);
      for (let i = 0; i < normAttr.array.length; i++) {
        normals[i] = -normAttr.array[i];
      }
      geom.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    }
  }

  switch (mode) {
    case 'flat':
      geom = toNonIndexed(geom);
      geom.computeVertexNormals();
      break;
    case 'smooth':
      geom.computeVertexNormals();
      break;
    case 'autoSmooth':
      geom = computeAutoSmoothNormals(geom, angleRad);
      break;
  }

  return geom;
}

function toNonIndexed(geom: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!geom.index) return geom;
  return geom.toNonIndexed();
}

function computeAutoSmoothNormals(
  geom: THREE.BufferGeometry,
  angleThreshold: number,
): THREE.BufferGeometry {
  const nonIndexed = toNonIndexed(geom);
  const posAttr = nonIndexed.getAttribute('position') as THREE.BufferAttribute;
  if (!posAttr) return nonIndexed;

  const count = posAttr.count;
  const triCount = count / 3;

  const faceNormals = new Float32Array(triCount * 3);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const cb = new THREE.Vector3();
  const ab = new THREE.Vector3();

  for (let f = 0; f < triCount; f++) {
    const i = f * 3;
    a.fromBufferAttribute(posAttr, i);
    b.fromBufferAttribute(posAttr, i + 1);
    c.fromBufferAttribute(posAttr, i + 2);
    cb.subVectors(c, b);
    ab.subVectors(a, b);
    cb.cross(ab).normalize();
    faceNormals[f * 3] = cb.x;
    faceNormals[f * 3 + 1] = cb.y;
    faceNormals[f * 3 + 2] = cb.z;
  }

  const cosThreshold = Math.cos(angleThreshold);

  // Group vertices by position
  const precision = 1e-4;
  const posMap = new Map<string, number[]>();
  for (let i = 0; i < count; i++) {
    const x = Math.round(posAttr.getX(i) / precision) * precision;
    const y = Math.round(posAttr.getY(i) / precision) * precision;
    const z = Math.round(posAttr.getZ(i) / precision) * precision;
    const key = `${x},${y},${z}`;
    if (!posMap.has(key)) posMap.set(key, []);
    posMap.get(key)!.push(i);
  }

  const normals = new Float32Array(count * 3);
  const fn = new THREE.Vector3();
  const neighborFn = new THREE.Vector3();
  const avg = new THREE.Vector3();

  for (const verts of posMap.values()) {
    for (const vi of verts) {
      const fi = Math.floor(vi / 3);
      fn.set(faceNormals[fi * 3], faceNormals[fi * 3 + 1], faceNormals[fi * 3 + 2]);
      avg.copy(fn);

      for (const vj of verts) {
        if (vj === vi) continue;
        const fj = Math.floor(vj / 3);
        if (fj === fi) continue;
        neighborFn.set(faceNormals[fj * 3], faceNormals[fj * 3 + 1], faceNormals[fj * 3 + 2]);
        if (fn.dot(neighborFn) >= cosThreshold) {
          avg.add(neighborFn);
        }
      }

      avg.normalize();
      normals[vi * 3] = avg.x;
      normals[vi * 3 + 1] = avg.y;
      normals[vi * 3 + 2] = avg.z;
    }
  }

  nonIndexed.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  return nonIndexed;
}

function buildAdjacency(geom: THREE.BufferGeometry): Map<number, Set<number>> {
  const adj = new Map<number, Set<number>>();
  const index = geom.index;

  function addEdge(a: number, b: number) {
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a)!.add(b);
    adj.get(b)!.add(a);
  }

  if (index) {
    const arr = index.array;
    for (let i = 0; i < arr.length; i += 3) {
      addEdge(arr[i], arr[i + 1]);
      addEdge(arr[i + 1], arr[i + 2]);
      addEdge(arr[i + 2], arr[i]);
    }
  } else {
    const count = geom.getAttribute('position').count;
    for (let i = 0; i < count; i += 3) {
      addEdge(i, i + 1);
      addEdge(i + 1, i + 2);
      addEdge(i + 2, i);
    }
  }
  return adj;
}

function applyOptimize(
  geom: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const ratio = Math.max(0.05, Math.min(1, params.ratio as number ?? 0.5));
  if (ratio >= 0.99) return geom;

  const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
  if (!posAttr) return geom;
  const count = posAttr.count;
  const targetCount = Math.max(3, Math.floor(count * ratio));

  if (targetCount >= count) return geom;

  // Simple vertex merging by spatial hashing
  const grid = 1.0 / (targetCount / count * 100);
  const merged = new Map<string, number>();
  const newPositions: number[] = [];
  const indexMap = new Int32Array(count);
  let nextIdx = 0;

  for (let i = 0; i < count; i++) {
    const x = posAttr.getX(i);
    const y = posAttr.getY(i);
    const z = posAttr.getZ(i);
    const key = `${Math.round(x / grid)},${Math.round(y / grid)},${Math.round(z / grid)}`;
    if (merged.has(key)) {
      indexMap[i] = merged.get(key)!;
    } else {
      merged.set(key, nextIdx);
      indexMap[i] = nextIdx;
      newPositions.push(x, y, z);
      nextIdx++;
    }
  }

  const newGeom = new THREE.BufferGeometry();
  newGeom.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));

  const oldIndex = geom.index;
  const newIndices: number[] = [];
  if (oldIndex) {
    for (let i = 0; i < oldIndex.count; i += 3) {
      const a = indexMap[oldIndex.getX(i)];
      const b = indexMap[oldIndex.getX(i + 1)];
      const c = indexMap[oldIndex.getX(i + 2)];
      if (a !== b && b !== c && a !== c) {
        newIndices.push(a, b, c);
      }
    }
  } else {
    for (let i = 0; i < count; i += 3) {
      const a = indexMap[i];
      const b = indexMap[i + 1];
      const c = indexMap[i + 2];
      if (a !== b && b !== c && a !== c) {
        newIndices.push(a, b, c);
      }
    }
  }

  newGeom.setIndex(newIndices);
  newGeom.computeVertexNormals();
  return newGeom;
}

function applyShell(
  geom: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const thickness = (params.thickness as number) ?? 0.01;
  const offset = (params.offset as number) ?? 1;
  const displacement = thickness * offset;
  if (!(Math.abs(displacement) > 1e-12)) return geom;

  // Vertex (smoothed) normals — use authored normals when present so Cap curvature stays smooth.
  if (!geom.getAttribute('normal')) {
    geom.computeVertexNormals();
  }
  const posAttr = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
  const normAttr = geom.getAttribute('normal') as THREE.BufferAttribute | undefined;
  if (!posAttr || !normAttr) return geom;

  const count = posAttr.count;
  const outerPositions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    outerPositions[i * 3] = posAttr.getX(i) + normAttr.getX(i) * displacement;
    outerPositions[i * 3 + 1] = posAttr.getY(i) + normAttr.getY(i) * displacement;
    outerPositions[i * 3 + 2] = posAttr.getZ(i) + normAttr.getZ(i) * displacement;
  }

  const outerGeom = geom.clone();
  outerGeom.setAttribute('position', new THREE.Float32BufferAttribute(outerPositions, 3));
  // Keep the smoothed normals used for displace — recomputeVertexNormals() destroys
  // authored Cap curvature and shows up as vertical waves in reflections.
  if (normAttr) {
    outerGeom.setAttribute('normal', normAttr.clone());
  } else {
    outerGeom.computeVertexNormals();
  }
  return outerGeom;
}

/**
 * Plane-slice mesh: keep one half relative to an axis-aligned cut plane.
 * Optional fan-cap closes the cut for a solid look.
 */
function applySlice(
  geom: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const axis = String(params.axis || 'y').toLowerCase();
  const offsetParam = Number(params.offset ?? 0);
  const keepPositive = params.keepPositive !== false;
  const fillCaps = params.fillCaps !== false;
  const fromCenter = params.fromCenter !== false;

  const src = geom.index ? geom.toNonIndexed() : geom.clone();
  const posAttr = src.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!posAttr || posAttr.count < 3) return geom;

  src.computeBoundingBox();
  const bb = src.boundingBox ?? new THREE.Box3().setFromBufferAttribute(posAttr);
  const center = new THREE.Vector3();
  bb.getCenter(center);

  const axisIdx = axis === 'x' ? 0 : axis === 'z' ? 2 : 1;
  const planeOrigin = fromCenter
    ? (axisIdx === 0 ? center.x : axisIdx === 1 ? center.y : center.z) + offsetParam
    : offsetParam;

  const sign = keepPositive ? 1 : -1;
  const side = (x: number, y: number, z: number) => {
    const c = axisIdx === 0 ? x : axisIdx === 1 ? y : z;
    return (c - planeOrigin) * sign;
  };

  const uvAttr = src.getAttribute('uv') as THREE.BufferAttribute | undefined;
  const outPos: number[] = [];
  const outUv: number[] = [];
  const capEdges: { a: THREE.Vector3; b: THREE.Vector3 }[] = [];

  const getP = (i: number) =>
    new THREE.Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
  const getUv = (i: number): [number, number] =>
    uvAttr ? [uvAttr.getX(i), uvAttr.getY(i)] : [0, 0];

  const pushVert = (p: THREE.Vector3, uv: [number, number]) => {
    outPos.push(p.x, p.y, p.z);
    outUv.push(uv[0], uv[1]);
  };

  const lerpVert = (
    a: THREE.Vector3,
    b: THREE.Vector3,
    uva: [number, number],
    uvb: [number, number],
  ): { p: THREE.Vector3; uv: [number, number] } => {
    const da = side(a.x, a.y, a.z);
    const db = side(b.x, b.y, b.z);
    const t = Math.abs(da - db) < 1e-10 ? 0.5 : da / (da - db);
    const p = a.clone().lerp(b, t);
    // Snap exactly onto plane
    if (axisIdx === 0) p.x = planeOrigin;
    else if (axisIdx === 1) p.y = planeOrigin;
    else p.z = planeOrigin;
    const uv: [number, number] = [
      uva[0] + (uvb[0] - uva[0]) * t,
      uva[1] + (uvb[1] - uva[1]) * t,
    ];
    return { p, uv };
  };

  const triCount = Math.floor(posAttr.count / 3);
  for (let t = 0; t < triCount; t++) {
    const i0 = t * 3;
    const i1 = t * 3 + 1;
    const i2 = t * 3 + 2;
    const p0 = getP(i0);
    const p1 = getP(i1);
    const p2 = getP(i2);
    const uv0 = getUv(i0);
    const uv1 = getUv(i1);
    const uv2 = getUv(i2);
    const s0 = side(p0.x, p0.y, p0.z);
    const s1 = side(p1.x, p1.y, p1.z);
    const s2 = side(p2.x, p2.y, p2.z);
    const keep0 = s0 >= -1e-8;
    const keep1 = s1 >= -1e-8;
    const keep2 = s2 >= -1e-8;
    const keepCount = (keep0 ? 1 : 0) + (keep1 ? 1 : 0) + (keep2 ? 1 : 0);

    if (keepCount === 3) {
      pushVert(p0, uv0);
      pushVert(p1, uv1);
      pushVert(p2, uv2);
      continue;
    }
    if (keepCount === 0) continue;

    // Build clipped polygon (3 or 4 verts) then fan triangulate
    type V = { p: THREE.Vector3; uv: [number, number] };
    const poly: V[] = [];
    const verts: V[] = [
      { p: p0, uv: uv0 },
      { p: p1, uv: uv1 },
      { p: p2, uv: uv2 },
    ];
    const keep = [keep0, keep1, keep2];
    const cutPts: THREE.Vector3[] = [];

    for (let e = 0; e < 3; e++) {
      const a = verts[e];
      const b = verts[(e + 1) % 3];
      const ka = keep[e];
      const kb = keep[(e + 1) % 3];
      if (ka) poly.push(a);
      if (ka !== kb) {
        const hit = lerpVert(a.p, b.p, a.uv, b.uv);
        poly.push(hit);
        cutPts.push(hit.p.clone());
      }
    }

    if (poly.length >= 3) {
      for (let i = 1; i < poly.length - 1; i++) {
        pushVert(poly[0].p, poly[0].uv);
        pushVert(poly[i].p, poly[i].uv);
        pushVert(poly[i + 1].p, poly[i + 1].uv);
      }
    }
    if (fillCaps && cutPts.length === 2) {
      capEdges.push({ a: cutPts[0], b: cutPts[1] });
    }
  }

  if (fillCaps && capEdges.length >= 3) {
    const centroid = new THREE.Vector3();
    for (const e of capEdges) {
      centroid.add(e.a);
      centroid.add(e.b);
    }
    centroid.multiplyScalar(1 / (capEdges.length * 2));
    if (axisIdx === 0) centroid.x = planeOrigin;
    else if (axisIdx === 1) centroid.y = planeOrigin;
    else centroid.z = planeOrigin;

    // Order edges angularly in the plane for a stable fan
    const uAxis = axisIdx === 0 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const n = new THREE.Vector3(axisIdx === 0 ? 1 : 0, axisIdx === 1 ? 1 : 0, axisIdx === 2 ? 1 : 0);
    // Flip cap normal to face outward (toward discarded side)
    if (keepPositive) n.negate();
    const vAxis = new THREE.Vector3().crossVectors(n, uAxis).normalize();
    uAxis.copy(new THREE.Vector3().crossVectors(vAxis, n).normalize());

    const pts = capEdges.map((e) => e.a);
    pts.sort((a, b) => {
      const aa = Math.atan2(a.clone().sub(centroid).dot(vAxis), a.clone().sub(centroid).dot(uAxis));
      const bb = Math.atan2(b.clone().sub(centroid).dot(vAxis), b.clone().sub(centroid).dot(uAxis));
      return aa - bb;
    });

    const capUv: [number, number] = [0.5, 0.5];
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      // Winding: centroid → a → b should face along n
      const ab = b.clone().sub(a);
      const ac = centroid.clone().sub(a);
      const cross = new THREE.Vector3().crossVectors(ab, ac);
      if (cross.dot(n) >= 0) {
        pushVert(centroid, capUv);
        pushVert(a, capUv);
        pushVert(b, capUv);
      } else {
        pushVert(centroid, capUv);
        pushVert(b, capUv);
        pushVert(a, capUv);
      }
    }
  }

  if (outPos.length < 9) {
    // Slice removed everything — return empty-ish but valid geometry
    const empty = new THREE.BufferGeometry();
    empty.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array([0, 0, 0, 0, 0, 0, 0, 0, 0]), 3));
    empty.computeVertexNormals();
    return empty;
  }

  // Non-indexed unique verts → flat shading. Weld so computeVertexNormals averages smoothly.
  let out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(outPos), 3));
  out.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(outUv), 2));
  try {
    out = mergeVertices(out, 1e-5);
  } catch {
    // keep non-indexed if merge fails
  }
  out.computeVertexNormals();
  out.computeBoundingBox();
  out.computeBoundingSphere();
  return out;
}

function applyUvPlanar(
  geom: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const axis = (params.axis as string) || 'y';
  const tilingU = (params.tilingU as number) || 1;
  const tilingV = (params.tilingV as number) || 1;
  const offsetU = (params.offsetU as number) || 0;
  const offsetV = (params.offsetV as number) || 0;

  const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
  if (!posAttr) return geom;

  const count = posAttr.count;
  const uvs = new Float32Array(count * 2);

  geom.computeBoundingBox();
  const bb = geom.boundingBox!;
  const size = new THREE.Vector3();
  bb.getSize(size);

  for (let i = 0; i < count; i++) {
    const x = posAttr.getX(i);
    const y = posAttr.getY(i);
    const z = posAttr.getZ(i);
    let u: number, v: number;
    if (axis === 'x') {
      u = (z - bb.min.z) / (size.z || 1);
      v = (y - bb.min.y) / (size.y || 1);
    } else if (axis === 'z') {
      u = (x - bb.min.x) / (size.x || 1);
      v = (y - bb.min.y) / (size.y || 1);
    } else {
      u = (x - bb.min.x) / (size.x || 1);
      v = (z - bb.min.z) / (size.z || 1);
    }
    uvs[i * 2] = u * tilingU + offsetU;
    uvs[i * 2 + 1] = v * tilingV + offsetV;
  }

  geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  return geom;
}

function applyUvBox(
  geom: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const tiling = (params.tiling as number) || 1;

  const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
  if (!posAttr) return geom;

  geom.computeVertexNormals();
  const normAttr = geom.getAttribute('normal') as THREE.BufferAttribute;
  if (!normAttr) return geom;

  const count = posAttr.count;
  const uvs = new Float32Array(count * 2);

  geom.computeBoundingBox();
  const bb = geom.boundingBox!;
  const size = new THREE.Vector3();
  bb.getSize(size);

  for (let i = 0; i < count; i++) {
    const x = posAttr.getX(i);
    const y = posAttr.getY(i);
    const z = posAttr.getZ(i);
    const nx = Math.abs(normAttr.getX(i));
    const ny = Math.abs(normAttr.getY(i));
    const nz = Math.abs(normAttr.getZ(i));

    let u: number, v: number;
    if (nx >= ny && nx >= nz) {
      u = (z - bb.min.z) / (size.z || 1);
      v = (y - bb.min.y) / (size.y || 1);
    } else if (ny >= nx && ny >= nz) {
      u = (x - bb.min.x) / (size.x || 1);
      v = (z - bb.min.z) / (size.z || 1);
    } else {
      u = (x - bb.min.x) / (size.x || 1);
      v = (y - bb.min.y) / (size.y || 1);
    }

    uvs[i * 2] = u * tiling;
    uvs[i * 2 + 1] = v * tiling;
  }

  geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  return geom;
}

function applyUvCylindrical(
  geom: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const axis = (params.axis as string) || 'y';
  const tiling = (params.tiling as number) || 1;

  const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
  if (!posAttr) return geom;

  const count = posAttr.count;
  const uvs = new Float32Array(count * 2);

  geom.computeBoundingBox();
  const bb = geom.boundingBox!;
  const size = new THREE.Vector3();
  bb.getSize(size);
  const center = new THREE.Vector3();
  bb.getCenter(center);

  for (let i = 0; i < count; i++) {
    const x = posAttr.getX(i) - center.x;
    const y = posAttr.getY(i) - center.y;
    const z = posAttr.getZ(i) - center.z;

    let angle: number, height: number;
    if (axis === 'y') {
      angle = Math.atan2(z, x);
      height = (posAttr.getY(i) - bb.min.y) / (size.y || 1);
    } else if (axis === 'x') {
      angle = Math.atan2(z, y);
      height = (posAttr.getX(i) - bb.min.x) / (size.x || 1);
    } else {
      angle = Math.atan2(y, x);
      height = (posAttr.getZ(i) - bb.min.z) / (size.z || 1);
    }

    uvs[i * 2] = ((angle / Math.PI + 1) * 0.5) * tiling;
    uvs[i * 2 + 1] = height * tiling;
  }

  geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  return geom;
}

function weldPosKey(x: number, y: number, z: number): string {
  return `${Math.round(x * 4096)}_${Math.round(y * 4096)}_${Math.round(z * 4096)}`;
}

function worldAxisVec(axis: string): THREE.Vector3 {
  if (axis === 'x') return new THREE.Vector3(1, 0, 0);
  if (axis === 'z') return new THREE.Vector3(0, 0, 1);
  return new THREE.Vector3(0, 1, 0);
}

/**
 * Per-island unwrap onto each chart's average surface plane (not world XYZ).
 * Neighbor faces stay in one chart until a crease steeper than `angle`.
 * `align=flow` puts V along the island's longest direction (ribbing follows the petal);
 * `align=up` puts V along the chosen world axis projected onto that plane.
 */
function applyUvOrganic(
  geometry: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const angleDeg = Math.min(180, Math.max(1, Number(params.angle ?? 55)));
  const tilingU = Number(params.tilingU ?? 1) || 1;
  const tilingV = Number(params.tilingV ?? 1) || 1;
  const offsetU = Number(params.offsetU ?? 0) || 0;
  const offsetV = Number(params.offsetV ?? 0) || 0;
  const align = String(params.align ?? 'flow');
  const worldUp = worldAxisVec(String(params.axis ?? 'y'));
  const cosCrease = Math.cos((angleDeg * Math.PI) / 180);

  const geom = geometry.index ? geometry.toNonIndexed() : geometry;
  const posAttr = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!posAttr || posAttr.count < 3) return geom;

  const triCount = Math.floor(posAttr.count / 3);
  if (triCount < 1) return geom;

  const weldOfVert = new Int32Array(posAttr.count);
  const weldPos: THREE.Vector3[] = [];
  const weldMap = new Map<string, number>();
  for (let i = 0; i < posAttr.count; i++) {
    const x = posAttr.getX(i);
    const y = posAttr.getY(i);
    const z = posAttr.getZ(i);
    const key = weldPosKey(x, y, z);
    let id = weldMap.get(key);
    if (id == null) {
      id = weldPos.length;
      weldMap.set(key, id);
      weldPos.push(new THREE.Vector3(x, y, z));
    }
    weldOfVert[i] = id;
  }

  const faceNormal: THREE.Vector3[] = new Array(triCount);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  for (let f = 0; f < triCount; f++) {
    const i0 = f * 3;
    a.fromBufferAttribute(posAttr, i0);
    b.fromBufferAttribute(posAttr, i0 + 1);
    c.fromBufferAttribute(posAttr, i0 + 2);
    faceNormal[f] = new THREE.Vector3().subVectors(b, a).cross(c.clone().sub(a));
    if (faceNormal[f]!.lengthSq() < 1e-20) faceNormal[f]!.set(0, 1, 0);
    else faceNormal[f]!.normalize();
  }

  const edgeFaces = new Map<string, number[]>();
  const edgeKey = (w0: number, w1: number) => (w0 < w1 ? `${w0}_${w1}` : `${w1}_${w0}`);
  for (let f = 0; f < triCount; f++) {
    const i0 = f * 3;
    const w = [weldOfVert[i0]!, weldOfVert[i0 + 1]!, weldOfVert[i0 + 2]!];
    for (let e = 0; e < 3; e++) {
      const k = edgeKey(w[e]!, w[(e + 1) % 3]!);
      let list = edgeFaces.get(k);
      if (!list) {
        list = [];
        edgeFaces.set(k, list);
      }
      list.push(f);
    }
  }

  const adj: number[][] = Array.from({ length: triCount }, () => []);
  for (const faces of edgeFaces.values()) {
    for (let i = 0; i < faces.length; i++) {
      for (let j = i + 1; j < faces.length; j++) {
        const f0 = faces[i]!;
        const f1 = faces[j]!;
        adj[f0]!.push(f1);
        adj[f1]!.push(f0);
      }
    }
  }

  const chartOf = new Int32Array(triCount);
  chartOf.fill(-1);
  let chartCount = 0;
  const queue: number[] = [];
  for (let seed = 0; seed < triCount; seed++) {
    if (chartOf[seed] >= 0) continue;
    const cid = chartCount++;
    chartOf[seed] = cid;
    queue.length = 0;
    queue.push(seed);
    for (let q = 0; q < queue.length; q++) {
      const f = queue[q]!;
      const n0 = faceNormal[f]!;
      for (const nb of adj[f]!) {
        if (chartOf[nb] >= 0) continue;
        if (n0.dot(faceNormal[nb]!) < cosCrease) continue;
        chartOf[nb] = cid;
        queue.push(nb);
      }
    }
  }

  type ChartAcc = {
    nSum: THREE.Vector3;
    welds: Set<number>;
  };
  const charts: ChartAcc[] = Array.from({ length: chartCount }, () => ({
    nSum: new THREE.Vector3(),
    welds: new Set<number>(),
  }));
  for (let f = 0; f < triCount; f++) {
    const ch = charts[chartOf[f]!]!;
    ch.nSum.add(faceNormal[f]!);
    const i0 = f * 3;
    ch.welds.add(weldOfVert[i0]!);
    ch.welds.add(weldOfVert[i0 + 1]!);
    ch.welds.add(weldOfVert[i0 + 2]!);
  }

  const chartU: THREE.Vector3[] = new Array(chartCount);
  const chartV: THREE.Vector3[] = new Array(chartCount);
  const chartOrigin: THREE.Vector3[] = new Array(chartCount);
  const tmp = new THREE.Vector3();
  const tAxis = new THREE.Vector3();
  const bAxis = new THREE.Vector3();

  for (let cid = 0; cid < chartCount; cid++) {
    const ch = charts[cid]!;
    const n = ch.nSum.clone();
    if (n.lengthSq() < 1e-12) n.set(0, 1, 0);
    else n.normalize();

    const origin = new THREE.Vector3();
    for (const wid of ch.welds) origin.add(weldPos[wid]!);
    origin.multiplyScalar(1 / Math.max(1, ch.welds.size));
    chartOrigin[cid] = origin;

    tAxis.crossVectors(n, worldUp);
    if (tAxis.lengthSq() < 1e-10) tAxis.crossVectors(n, new THREE.Vector3(1, 0, 0));
    if (tAxis.lengthSq() < 1e-10) tAxis.crossVectors(n, new THREE.Vector3(0, 0, 1));
    tAxis.normalize();
    bAxis.crossVectors(n, tAxis).normalize();

    let uAxis: THREE.Vector3;
    let vAxis: THREE.Vector3;
    if (align === 'up') {
      uAxis = tAxis.clone();
      vAxis = bAxis.clone();
      if (vAxis.dot(worldUp) < 0) {
        vAxis.negate();
        uAxis.negate();
      }
    } else {
      let sxx = 0;
      let sxy = 0;
      let syy = 0;
      for (const wid of ch.welds) {
        tmp.copy(weldPos[wid]!).sub(origin);
        const x = tmp.dot(tAxis);
        const y = tmp.dot(bAxis);
        sxx += x * x;
        sxy += x * y;
        syy += y * y;
      }
      const trace = sxx + syy;
      const disc = Math.sqrt(Math.max(0, (sxx - syy) * (sxx - syy) + 4 * sxy * sxy));
      const l1 = 0.5 * (trace + disc);
      let vx = sxy;
      let vy = l1 - sxx;
      if (vx * vx + vy * vy < 1e-16) {
        vx = 1;
        vy = 0;
      } else {
        const len = Math.hypot(vx, vy);
        vx /= len;
        vy /= len;
      }
      vAxis = tAxis.clone().multiplyScalar(vx).addScaledVector(bAxis, vy).normalize();
      if (vAxis.dot(worldUp) < 0) vAxis.negate();
      uAxis = new THREE.Vector3().crossVectors(vAxis, n).normalize();
    }
    chartU[cid] = uAxis;
    chartV[cid] = vAxis;
  }

  const uvs = new Float32Array(posAttr.count * 2);
  const p = new THREE.Vector3();
  for (let f = 0; f < triCount; f++) {
    const cid = chartOf[f]!;
    const uA = chartU[cid]!;
    const vA = chartV[cid]!;
    const o = chartOrigin[cid]!;
    for (let k = 0; k < 3; k++) {
      const i = f * 3 + k;
      p.fromBufferAttribute(posAttr, i).sub(o);
      uvs[i * 2] = p.dot(uA) * tilingU + offsetU;
      uvs[i * 2 + 1] = p.dot(vA) * tilingV + offsetV;
    }
  }

  geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  return geom;
}

function applyUvTransform(
  geometry: THREE.BufferGeometry,
  params: Record<string, number | boolean | string>,
): THREE.BufferGeometry {
  const geom = geometry.clone();
  const uvAttr = geom.getAttribute('uv') as THREE.BufferAttribute | undefined;
  if (!uvAttr) return geom;

  const tilingU = Number(params.tilingU ?? 1);
  const tilingV = Number(params.tilingV ?? 1);
  const offsetU = Number(params.offsetU ?? 0);
  const offsetV = Number(params.offsetV ?? 0);
  const rotDeg = Number(params.rotation ?? 0);
  const r = (rotDeg * Math.PI) / 180;
  const cosR = Math.cos(r);
  const sinR = Math.sin(r);

  const count = uvAttr.count;
  for (let i = 0; i < count; i++) {
    const u = uvAttr.getX(i);
    const v = uvAttr.getY(i);
    const cu = u - 0.5;
    const cv = v - 0.5;
    const ru = cu * cosR - cv * sinR + 0.5;
    const rv = cu * sinR + cv * cosR + 0.5;
    uvAttr.setXY(i, ru * tilingU + offsetU, rv * tilingV + offsetV);
  }

  uvAttr.needsUpdate = true;
  return geom;
}
